import { useTranslation } from "react-i18next";
import Select, { StylesConfig } from "react-select";
import { useTheme } from "@packages/hooks/useTheme";
import {
  CourseData,
  GradeOptions,
  LessonPlanGradeOptions,
} from "@packages/constant/CourseData";
import { Text } from "@core/text";
import { UnitOutcomeSelector } from "@features/assignments/AssignmentGenerator/components/unit-outcome-selector";
import { BaseChatDtoCourse } from "@packages/api/madlenAPI.schemas";

interface SelectOption {
  value: number | string;
  label: string;
}

interface BasicInfoStepProps {
  courseType: string;
  grade: number;
  onLessonChange: (value: string) => void;
  onGradeLevelChange: (value: number) => void;
  isMaarif?: boolean;
  isUbd?: boolean;
  lessonCount?: number;
  onLessonCountChange?: (value: number) => void;
  selectedOutcomes?: { id: string; text: string }[];
  onOutcomesChange?: (outcomes: { id: string; text: string }[]) => void;
}

const BasicInfoStep: React.FC<BasicInfoStepProps> = ({
  courseType,
  grade,
  onLessonChange,
  onGradeLevelChange,
  isMaarif,
  isUbd = false,
  lessonCount = 9,
  onLessonCountChange,
  selectedOutcomes = [],
  onOutcomesChange,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();

  // Handle learning outcomes change
  const handleOutcomesChange = (
    outcomeIds: number[],
    outcomeTexts?: string[],
  ) => {
    if (onOutcomesChange) {
      const outcomes = outcomeIds.map((id, index) => ({
        id: id.toString(),
        text: outcomeTexts?.[index] || "",
      }));
      onOutcomesChange(outcomes);
    }
  };

  const selectStyles: StylesConfig<SelectOption, false> = {
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    control: (base, state) => ({
      ...base,
      backgroundColor: theme === "dark" ? "#1F2937" : "#FFFFFF",
      borderColor: state.isFocused
        ? theme === "dark"
          ? "#059669"
          : "#059669"
        : theme === "dark"
          ? "#4B5563"
          : "#E5E7EB",
      borderWidth: "1px",
      boxShadow: state.isFocused
        ? theme === "dark"
          ? "0 0 0 1px #059669"
          : "0 0 0 1px #059669"
        : "none",
      "&:hover": {
        borderColor: theme === "dark" ? "#6B7280" : "#D1D5DB",
      },
      minHeight: "42px",
      cursor: "pointer",
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: theme === "dark" ? "#1F2937" : "#FFFFFF",
      border: theme === "dark" ? "1px solid #4B5563" : "1px solid #E5E7EB",
      boxShadow:
        theme === "dark"
          ? "0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)"
          : "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
      borderRadius: "8px",
      overflow: "hidden",
    }),
    menuList: (base) => ({
      ...base,
      padding: "4px",
      maxHeight: "200px",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#059669"
        : state.isFocused
          ? theme === "dark"
            ? "#374151"
            : "#F9FAFB"
          : "transparent",
      color: state.isSelected
        ? "#FFFFFF"
        : theme === "dark"
          ? "#F9FAFB"
          : "#111827",
      padding: "10px 12px",
      cursor: "pointer",
      borderRadius: "6px",
      margin: "2px 0",
      fontSize: "14px",
      "&:hover": {
        backgroundColor: state.isSelected
          ? "#047857"
          : theme === "dark"
            ? "#374151"
            : "#F3F4F6",
      },
      "&:active": {
        backgroundColor: state.isSelected
          ? "#065f46"
          : theme === "dark"
            ? "#4B5563"
            : "#E5E7EB",
      },
    }),
    singleValue: (base) => ({
      ...base,
      color: theme === "dark" ? "#F9FAFB" : "#111827",
      fontSize: "14px",
    }),
    placeholder: (base) => ({
      ...base,
      color: theme === "dark" ? "#9CA3AF" : "#6B7280",
      fontSize: "14px",
    }),
    input: (base) => ({
      ...base,
      color: theme === "dark" ? "#F9FAFB" : "#111827",
      fontSize: "14px",
    }),
    indicatorSeparator: (base) => ({
      ...base,
      backgroundColor: theme === "dark" ? "#4B5563" : "#E5E7EB",
    }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: theme === "dark" ? "#9CA3AF" : "#6B7280",
      padding: "8px",
      "&:hover": {
        color: theme === "dark" ? "#D1D5DB" : "#374151",
      },
      transform: state.selectProps.menuIsOpen
        ? "rotate(180deg)"
        : "rotate(0deg)",
      transition: "transform 0.2s ease",
    }),
    clearIndicator: (base) => ({
      ...base,
      color: theme === "dark" ? "#9CA3AF" : "#6B7280",
      padding: "8px",
      "&:hover": {
        color: theme === "dark" ? "#F87171" : "#EF4444",
      },
    }),
    noOptionsMessage: (base) => ({
      ...base,
      color: theme === "dark" ? "#9CA3AF" : "#6B7280",
      fontSize: "14px",
      padding: "12px",
    }),
    loadingMessage: (base) => ({
      ...base,
      color: theme === "dark" ? "#9CA3AF" : "#6B7280",
      fontSize: "14px",
      padding: "12px",
    }),
  };

  const isPreSchool = grade === -3 || grade === -2;

  const courseOptions: SelectOption[] = Object.entries(CourseData)
    .filter(
      ([, course]) =>
        course.visible(grade) &&
        (isMaarif ? course.hasMaarifOutcomes : course.hasOutcomes),
    )
    .map(([key, course]) => ({
      value: key,
      label: t(`courseType.${course.name(grade)}`),
    }));

  const getGradeSelectValue = () => {
    if (!grade) return null;

    if (grade === -3) {
      return {
        value: -3,
        label: t("courseType.MONTH_36_48"),
      };
    }

    if (grade === -2) {
      return {
        value: -2,
        label: t("courseType.MONTH_48_60"),
      };
    }

    if (grade === -4) {
      return {
        value: -4,
        label: t("courseType.PRE_SCHOOL"),
      };
    }

    return {
      value: grade,
      label: t("dailyLessonPlan.grade", { grade: grade }),
    };
  };

  const getCourseSelectValue = () => {
    if (!courseType) return null;

    const courseData = CourseData[courseType as keyof typeof CourseData];
    if (!courseData) return null;

    return {
      value: courseType,
      label: t(`courseType.${courseData.name(grade)}`),
    };
  };

  const handleGradeChange = (selected: SelectOption | null) => {
    const gradeValue = selected ? Number(selected.value) : 0;
    onGradeLevelChange(gradeValue);

    // Auto-select course for preschool grades
    if (gradeValue === -3) {
      onLessonChange("MONTH_36_48");
      console.log("MONTH_36_48");
    } else if (gradeValue === -2) {
      onLessonChange("MONTH_48_60");
      console.log("MONTH_48_60");
    } else {
      // Clear course selection when grade changes
      onLessonChange("");
    }
  };

  const handleLessonChange = (selected: SelectOption | null) => {
    onLessonChange(selected ? String(selected.value) : "");
  };

  return (
    <div className="space-y-6">
      {/* Grade Level Selection */}
      <div>
        <label
          htmlFor="gradeLevel"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          {t("dailyLessonPlan.gradeLevel")}
        </label>
        <Select<SelectOption, false>
          id="gradeLevel"
          options={!isMaarif ? LessonPlanGradeOptions(t) : GradeOptions(t)}
          value={getGradeSelectValue()}
          onChange={handleGradeChange}
          styles={selectStyles}
          placeholder={t("dailyLessonPlan.selectGrade")}
          isClearable
          isSearchable={false}
          menuPortalTarget={document.body}
          menuPosition="absolute"
          menuPlacement="auto"
          menuShouldScrollIntoView={false}
          classNamePrefix="grade-select"
        />
      </div>

      {/* Lesson Selection */}
      <div>
        <label
          htmlFor="lesson"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
        >
          {t("dailyLessonPlan.lesson")}
        </label>
        <Select<SelectOption, false>
          id="lesson"
          options={courseOptions}
          value={getCourseSelectValue()}
          onChange={handleLessonChange}
          styles={selectStyles}
          placeholder={t("dailyLessonPlan.selectLesson")}
          isClearable
          isSearchable={false}
          isDisabled={!grade || isPreSchool}
          menuPortalTarget={document.body}
          menuPosition="absolute"
          menuPlacement="auto"
          menuShouldScrollIntoView={false}
          classNamePrefix="lesson-select"
        />
        {!grade && (
          <Text variant={"p"} status="error" size={"sm"} className="mt-2">
            {t("dailyLessonPlan.selectGradeFirst")}
          </Text>
        )}
      </div>

      {/* Lesson Count Selection - UBD specific */}
      {isUbd && onLessonCountChange && (
        <div>
          <label
            htmlFor="lessonCount"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            Ders Sayısı
          </label>
          <div className="flex items-center space-x-2 max-w-xs">
            <button
              type="button"
              onClick={() => onLessonCountChange(Math.max(1, lessonCount - 1))}
              className="w-8 h-8 rounded-md bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 font-medium transition-colors text-sm"
            >
              -
            </button>
            <div className="relative">
              <input
                type="number"
                id="lessonCount"
                value={lessonCount}
                onChange={(e) => onLessonCountChange(Number(e.target.value))}
                min="1"
                max="20"
                className="w-28 px-3 py-2 pr-12 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-800 dark:text-gray-100 text-sm text-center font-medium [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-500 dark:text-gray-400 pointer-events-none">
                ders
              </div>
            </div>
            <button
              type="button"
              onClick={() => onLessonCountChange(Math.min(20, lessonCount + 1))}
              className="w-8 h-8 rounded-md bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-300 font-medium transition-colors text-sm"
            >
              +
            </button>
          </div>
          <Text
            variant="p"
            size="sm"
            className="text-amber-600 dark:text-amber-400 mt-1"
          >
            💡 İyi bir UbD planı için 9-10 ders saatine yayılan bir ünite
            seçmenizi öneriyoruz
          </Text>
        </div>
      )}

      {/* Learning Outcomes Selection */}
      {courseType && grade && onOutcomesChange && (
        <div>
          <label
            htmlFor="outcomes"
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
          >
            📚 Öğrenme Çıktıları Seçimi
          </label>
          <Text
            variant="p"
            size="sm"
            className="text-gray-600 dark:text-gray-300 mb-4"
          >
            Bu ders için uygun öğrenme çıktılarını seçiniz:
          </Text>
          <UnitOutcomeSelector
            grade={grade}
            courseType={courseType as BaseChatDtoCourse}
            isMaarif={isMaarif}
            selectedOutcomes={selectedOutcomes.map((o) => Number(o.id))}
            setSelectedOutcomes={handleOutcomesChange}
          />
        </div>
      )}

      {/* Helper Text */}
      <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-emerald-400"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <Text variant="p" status="success" size="sm">
              {isUbd
                ? "UbD planınızın standartları için kullanılmak üzere MEB Maarif programından öğrenme çıktıları ve süreç bileşenlerini seçin"
                : t("dailyLessonPlan.basicInfoHelper")}
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BasicInfoStep;
